
import React, { useState, useEffect } from 'react';
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandInput, 
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Plus, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Portfolio } from '@/schemas/portfolio'; // Updated import
import { useBusinessDropdown } from '@/hooks/useBusinessDropdown';
import { getAuth } from 'firebase/auth';

interface AddBusinessDropdownProps {
  portfolios: Portfolio[];
  onCreateNewPortfolio: () => void;
  onSelectPortfolio: (portfolioId: string) => void;
}

const AddBusinessDropdown: React.FC<AddBusinessDropdownProps> = ({
  portfolios,
  onCreateNewPortfolio,
  onSelectPortfolio
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  const userEmail = auth.currentUser?.email;
  
  const {
    open,
    setOpen,
    searchValue,
    setSearchValue,
    selectedValue,
    filteredOptions,
    handleSelectPortfolio,
    ownedPortfolios
  } = useBusinessDropdown(portfolios, onSelectPortfolio);

  const handleCreateNewPortfolio = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateNewPortfolio();
    setOpen(false);
  };

  // Debug logging for user identification and portfolio data
  useEffect(() => {
    console.log(`AddBusinessDropdown - Current user ID: ${userId}, email: ${userEmail}`);
    console.log(`AddBusinessDropdown - Available portfolios: ${portfolios.length}`);
    
    // Log the first portfolio if available
    if (portfolios.length > 0) {
      console.log(`AddBusinessDropdown - First portfolio: ${JSON.stringify({
        id: portfolios[0].id,
        name: portfolios[0].name, // Use portfolio.name
        memberCount: portfolios[0].members?.length || 0
      })}`);
    }
  }, [userId, userEmail, portfolios]);

  // More responsive loading state based on owned portfolios being ready
  useEffect(() => {
    // Start with loading state
    if (portfolios.length > 0) {
      // If we have owned portfolios, we can stop loading
      if (ownedPortfolios.length > 0) {
        console.log(`AddBusinessDropdown - Found ${ownedPortfolios.length} owned portfolios, stopping loading`);
        setIsLoading(false);
      } else {
        // Set a reasonable timeout for loading to prevent endless loading state
        const timer = setTimeout(() => {
          console.log('AddBusinessDropdown - Loading timeout reached, stopping loading');
          setIsLoading(false);
        }, 1500); // Reduced from 2 seconds to 1.5 seconds for better UX
      
        return () => clearTimeout(timer);
      }
    } else {
      // If we have no portfolios at all, we're not loading, just empty
      setIsLoading(false);
    }
  }, [portfolios, ownedPortfolios]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          className="bg-black hover:bg-gray-900 text-white rounded-md gap-2 h-9 py-1 px-3 text-sm"
          variant="default"
          role="combobox"
          aria-expanded={open}
        >
          <Plus size={14} />
          Add New Business
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-white" 
        align="start"
        sideOffset={4}
      >
        <Command>
          <CommandInput 
            placeholder="Search portfolios..." 
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                <span className="ml-2 text-sm text-gray-500">Loading portfolios...</span>
              </div>
            ) : ownedPortfolios.length === 0 ? (
              <CommandEmpty>
                <div className="flex flex-col items-center justify-center py-6">
                  <p className="text-sm text-gray-500 mb-2">You need to create a portfolio first or be added as an owner</p>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={handleCreateNewPortfolio}
                  >
                    <Plus size={16} />
                    Create New Portfolio
                  </Button>
                </div>
              </CommandEmpty>
            ) : (
              <>
                <CommandEmpty>
                  <div className="flex flex-col items-center justify-center py-6">
                    <p className="text-sm text-gray-500 mb-2">No portfolio found</p>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={handleCreateNewPortfolio}
                    >
                      <Plus size={16} />
                      Create New Portfolio
                    </Button>
                  </div>
                </CommandEmpty>
                
                {filteredOptions.length > 0 && (
                  <>
                    <CommandItem
                      className="flex items-center py-3 pl-3"
                      onSelect={() => {
                        handleCreateNewPortfolio({ stopPropagation: () => {} } as React.MouseEvent);
                      }}
                    >
                      <Plus size={16} className="mr-2" />
                      <span>Create New Portfolio</span>
                    </CommandItem>
                    
                    <CommandGroup heading="Portfolios You Own">
                      {filteredOptions.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => handleSelectPortfolio(option.value, option.label)}
                          className="flex items-center justify-between py-3"
                        >
                          <span>{option.label}</span>
                          {selectedValue === option.value && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AddBusinessDropdown;
